import React, {useCallback, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import gridStyles from '../../../../GridStyles.module.scss';
import {bindActionCreators, Dispatch} from 'redux';
import {ConfirmationDialog} from '../../../../../components/util/ConfirmationDialog/ConfirmationDialog';
import IconButton from '../../../../../components/util/widgets/IconButton/IconButton';
import { Paper, Button } from '@mantine/core';
import StyledMantineTable, { type MRT_ColumnDef } from '../../../../../components/util/StyledMantineTable/StyledMantineTable';
import {useSearchParams} from 'react-router-dom';
import {CommonState} from '../../../../../redux';
import {Item, itemStore} from '../../../../../redux/entities/inventory/item';
import {formatCentsToUSD, propertyOf} from '../../../../../util';
import ItemModal from './ItemModal/ItemModal';
import CategoriesModal from './CategoriesModal/CategoriesModal';
import {categoryStore} from '../../../../../redux/entities/inventory/category';
import {AppTheme} from '../../../../../appTheme';
import ItemPicture from '../../../../../components/util/ItemPicture/ItemPicture';
import {isNullOrWhitespace} from '../../../../../util/string';
import useStandardPrint from '../../../../../hooks/useStandardPrint/useStandardPrint';
import TransferModal from './TransferModal/TransferModal';
import AdministratorComponent from "../../../../../components/util/RoleGatedComponent/AdministratorComponent";
import {RoleProtectedComponent} from "../../../../../components/util/RoleProtectedRoute/RoleProtectedComponent";
import AuditLogLoader from "../../../../private/AuditLog/AuditLogLoader";

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

function InventoryGrid(props: Props) {
  const {inventory, getItemById, getCategoryById, actions: {archiveItem, upsertItem}} = props;
  const [isDeleting, setIsDeleting] = useState('');
  const [isActivating, setIsActivating] = useState('');
  const [editingItemData, setEditingItemData] = useState<{editing: boolean; id: string}>({editing: false, id: ''});
  const [viewingItemId, setViewingItemId] = useState<string>('');
  const [editingCategories, setEditingCategories] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  // Local audit log drawer state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntityId, setAuditEntityId] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();

  const printRef = useRef(null);
  const handleprint = useStandardPrint(printRef);

  const getFieldName = propertyOf<Item>;


  const actionsFormatter = useCallback((cell: any, item: Item) => {
    return (
      <div className={gridStyles['table-actions-wrapper']}>
        <div>
          <AdministratorComponent>
            <IconButton
              icon={'history'}
              styles={{color: AppTheme.colors.themePurple}}
              iconToolTipText={'History'}
              onClick={() => { setAuditEntityId(item.id); setAuditOpen(true); }}
            />
          </AdministratorComponent>
        </div>
        <div>
          <IconButton
            icon={'eye'}
            styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
            iconToolTipText={'View'}
            onClick={() => setViewingItemId(item.id)}
          />
        </div>
        <RoleProtectedComponent allowedRoles={['Administrator', 'Volunteer']}>
          <div>
            <IconButton
              icon={'pen'}
              styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
              iconToolTipText={'Edit'}
              onClick={() => setEditingItemData({editing: true, id: item.id})}
            />
          </div>
        </RoleProtectedComponent>
        <AdministratorComponent>
          <div>
            <IconButton
              icon={'trash-alt'}
              styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
              iconToolTipText={'Delete'}
              onClick={() => setIsDeleting(item.id)}
            />
          </div>
        </AdministratorComponent>
      </div>
    );
  }, []);

  const pictureFormatter = (cell: any, item: Item) => {
    if (isNullOrWhitespace(item.imagePath)) return null;
    return (
      <ItemPicture itemId={item.id} editable={false} style={{display: 'block', height: '2rem', width: '2rem', borderRadius: '50%'}}/>
    );
  };

  const columns: MRT_ColumnDef<Item>[] = useMemo(() => [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      enableSorting: true,
    },
    {
      id: 'warehouseQuantity',
      header: 'Warehouse Quantity',
      accessorKey: 'warehouseQuantity',
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'storeQuantity',
      header: 'Store Quantity',
      accessorKey: 'storeQuantity',
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'estimatedCost',
      header: 'Unit Cost',
      accessorKey: 'estimatedCost',
      Cell: ({ cell }) => formatCentsToUSD(cell.getValue<number>() ?? 0),
      enableSorting: true,
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorFn: ( row ) => formatCentsToUSD((row.warehouseQuantity + row.storeQuantity) * row.estimatedCost),
      enableSorting: true,
    },
    {
      id: 'maxOrderSize',
      header: 'Max Order Size',
      accessorKey: 'maxOrderSize',
      accessorFn: (row) => row.restrictOrderSize ? row.maxOrderSize : 'N/A',
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'category',
      header: 'Category',
      accessorFn: (row) => getCategoryById(row.categoryId)?.name ?? '',
      enableSorting: true,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessorFn: () => '',
      Cell: ({ row }) => actionsFormatter(null, row.original as Item),
      size: 0,
      minSize: 0,
      maxSize: 0,
    },
  ], [getCategoryById, actionsFormatter]);

  return (
    <>
      <Paper withBorder shadow="sm" radius="md" p="sm" className={gridStyles['responsive-table-wrapper']}>
        <StyledMantineTable
          columns={columns}
          data={inventory}
          printRef={printRef}
          initialState={{ columnVisibility: { id: false }, pagination: {pageSize: 15}}}
          topToolbarActions={
            <div className={gridStyles['button-group']}>
              <RoleProtectedComponent allowedRoles={['Administrator', 'Volunteer']}>
                <Button
                  onClick={() => setEditingItemData({editing: true, id: ''})}
                  style={{backgroundColor: AppTheme.colors.themePurple}}
                >
                  Add Item
                </Button>
              </RoleProtectedComponent>
              <RoleProtectedComponent allowedRoles={['Administrator', 'Volunteer']}>
                <Button
                  onClick={() => setTransferOpen(true)}
                  style={{backgroundColor: AppTheme.colors.themePurple}}
                >
                  Transfer Inventory
                </Button>
              </RoleProtectedComponent>
              <RoleProtectedComponent allowedRoles={['Administrator']}>
                <Button
                  onClick={() => setEditingCategories(true)}
                  style={{backgroundColor: AppTheme.colors.themePurple}}
                >
                  Edit Categories
                </Button>
              </RoleProtectedComponent>
              <Button onClick={handleprint} style={{backgroundColor: AppTheme.colors.themePurple}}>
                Print Table
              </Button>
            </div>
          }
          mantineTableProps={{
            striped: true,
            highlightOnHover: true,
            withColumnBorders: true,
            className: gridStyles['table-auto'],
          }}
        />
      </Paper>

      {isDeleting !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            await archiveItem(isDeleting);
            setIsDeleting('');
          }}
          onDecline={async () => { setIsDeleting(''); }}
          open={isDeleting !== ''}
          prompt={`Are you sure you want to remove the item "${getItemById(isDeleting)?.name}?"`}
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}
      {isActivating !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            setIsActivating('');
          }}
          onDecline={async () => { setIsActivating(''); }}
          open={isActivating !== ''}
          prompt='Are you sure you want to activate this item?'
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}
      {editingItemData.editing ? <ItemModal
        show={editingItemData.editing}
        existing={getItemById(editingItemData.id)}
        onSubmit={() => setEditingItemData({editing: false, id: ''})}
        onCancel={() => setEditingItemData({editing: false, id: ''})}
      /> : null}

      {/* View-only Item modal, opened via eye icon */}
      {viewingItemId && getItemById(viewingItemId) ? (
        <ItemModal
          show={true}
          existing={getItemById(viewingItemId)}
          readOnlyInitial={true}
          onSubmit={() => setViewingItemId('')}
          onCancel={() => setViewingItemId('')}
        />
      ) : null}
      <RoleProtectedComponent allowedRoles={['Administrator']}>
        {editingCategories ? <CategoriesModal
          show={editingCategories}
          onCancel={() => setEditingCategories(false)}
          onSubmit={() => setEditingCategories(false)}
        /> : null}
      </RoleProtectedComponent>
      <TransferModal
        opened={transferOpen}
        onClose={() => setTransferOpen(false)}
        inventory={inventory}
        onSuccess={(updated) => props.actions.upsertItem(updated)}
      />

      <AuditLogLoader
        opened={auditOpen}
        onClose={() => setAuditOpen(false)}
        entityId={auditEntityId}
      />
    </>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    archiveItem: itemStore.actions.archiveItem,
    save: itemStore.actions.save,
    upsertItem: itemStore.actions.upsert
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  inventory: itemStore.selectors.unarchivedItems(state),
  getItemById: itemStore.selectors.getById(state),
  getCategoryById: categoryStore.selectors.getById(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(InventoryGrid);
